import { Injectable, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter, map } from "rxjs";
import { agentRoute } from "../agent-chat/agent-view-route";
import { AgentChatService } from "../agent-chat/agent-chat.service";
import { ViewportService } from "../common/services/viewport.service";
import { PortfolioStateService } from "../parse-analyze-track/services/portfolio-state.service";
import { WALKTHROUGHS_ROUTE, needsPortfolio, walkthroughRequirements, type WalkthroughEntry } from "./walkthrough-catalog";
import { WalkthroughRunnerService } from "./walkthrough-runner.service";
import { WalkthroughTaskService } from "./walkthrough-task.service";

export type LaunchResult = "started" | "not-on-walkthroughs-page" | "needs-portfolio" | "nothing-to-show";

/**
 * The ONE way a user starts a tour or walkthrough: the Walkthroughs page's
 * cards and the assistant's card (which only appears on that page) both call
 * launch(). It refuses anywhere but the Walkthroughs page, so "start only from
 * Walkthroughs" holds in code, not just in the assistant's prompt
 * (agents/guided-walkthroughs-plan.md, "Walkthroughs hub").
 *
 * It takes the user to the walkthrough's page (with the selected portfolio)
 * and starts it there. Navigating is the user's own Start press; the
 * walkthrough itself still never clicks, types or saves.
 */
@Injectable({ providedIn: "root" })
export class WalkthroughLauncherService {
  private readonly router = inject(Router);
  private readonly task = inject(WalkthroughTaskService);
  private readonly runner = inject(WalkthroughRunnerService);
  private readonly chat = inject(AgentChatService);
  private readonly viewport = inject(ViewportService);
  private readonly portfolios = inject(PortfolioStateService);

  /** True on the Walkthroughs page, the only place launch() works. */
  onWalkthroughsPage(): boolean {
    return isWalkthroughsUrl(this.router.url);
  }

  /** The same, as a signal for templates (the assistant's card switches to "Open Walkthroughs" elsewhere). */
  readonly onWalkthroughsPageSignal = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => isWalkthroughsUrl(event.urlAfterRedirects)),
    ),
    { initialValue: isWalkthroughsUrl(this.router.url) },
  );

  /** The portfolio a walkthrough would open, or null when none is selected. */
  selectedPortfolioId(): number | null {
    return this.portfolios.getSelected()?.id ?? null;
  }

  /** True once the user's own portfolio list has loaded and is empty (a brand-new account). */
  ownsNoPortfolios(): boolean {
    return this.portfolios.owned()?.length === 0;
  }

  /** True once the user's own portfolio list has loaded with at least one portfolio (false while it loads). */
  ownsAnyPortfolio(): boolean {
    return (this.portfolios.owned()?.length ?? 0) > 0;
  }

  /** The selected portfolio, else the user's first own one: where a page link opens. */
  portfolioForPage(): number | null {
    return this.selectedPortfolioId() ?? this.portfolios.owned()?.[0]?.id ?? null;
  }

  /**
   * Open an app page from the Walkthroughs page (a tile with no walkthrough
   * yet). Hub-only like launch(); a page under /portfolio opens with a portfolio.
   */
  async openPage(route: string): Promise<"opened" | "not-on-walkthroughs-page" | "needs-portfolio"> {
    if (!this.onWalkthroughsPage()) return "not-on-walkthroughs-page";
    const onPortfolio = route === "/portfolio" || route.startsWith("/portfolio/");
    const pid = onPortfolio ? this.portfolioForPage() : null;
    if (onPortfolio && pid === null) return "needs-portfolio";
    if (this.viewport.isPhone()) this.chat.close();
    await this.router.navigate(appCommands(route), { queryParams: pid !== null ? { pid } : {} });
    return "opened";
  }

  /** Start `entry` (at `resumeStepId` for Continue). */
  async launch(entry: WalkthroughEntry, options: { resumeStepId?: string | null } = {}): Promise<LaunchResult> {
    if (!this.onWalkthroughsPage()) return "not-on-walkthroughs-page";
    // The page's lock (its group needs a portfolio), enforced here too: Vega's
    // chat card starts walkthroughs that begin on /portfolios, which the
    // route check below doesn't cover.
    if (walkthroughRequirements(entry.id).includes("portfolio") && !this.ownsAnyPortfolio()) return "needs-portfolio";
    const pid = this.selectedPortfolioId();
    if (needsPortfolio(entry) && pid === null) return "needs-portfolio";

    // One guide at a time.
    this.task.exit();
    this.runner.stop();
    // On a phone the panel covers the page; the step popover carries the steps
    // and the panel is one tap away for questions.
    if (this.viewport.isPhone()) this.chat.close();

    const navigated = await this.router.navigate(appCommands(entry.route), {
      queryParams: needsPortfolio(entry) && pid !== null ? { pid } : {},
    });
    if (navigated === false) return "nothing-to-show";
    if (entry.kind === "task") {
      this.task.start(entry, options.resumeStepId);
      return "started";
    }
    return (await this.runner.startWhenReady(entry)) ? "started" : "nothing-to-show";
  }
}

function isWalkthroughsUrl(url: string): boolean {
  const [path] = url.split("#")[0].split("?");
  return agentRoute(path.length > 1 ? path.replace(/\/+$/, "") : path) === WALKTHROUGHS_ROUTE;
}

/** Assistant-facing route → router commands (the portfolio tabs live under /portfolio/:tab). */
export function appCommands(route: string): string[] {
  if (route === "/portfolio") return ["/parse-analyze-track/portfolio", "portfolio"];
  if (route.startsWith("/portfolio/")) return ["/parse-analyze-track/portfolio", route.slice("/portfolio/".length)];
  return ["/parse-analyze-track", ...route.slice(1).split("/")];
}
